import { tv } from "../../variants/tv.js";
const inputTextVariants = tv({
    slots: {
        root: 'group relative isolate flex flex-col gap-1 antialiased transition duration-200',
        labelSlot: 'relative order-first cursor-text truncate text-body3 text-primary-13 transition duration-200',
        inputWrapper: [
            'relative grid grid-cols-1 content-center gap-3 rounded-xl border bg-primary-1',
            'border-primary-6 focus-within:border-primary-8',
            'group-data-focused:shadow-input-focus'
        ],
        input: [
            'col-start-1 row-start-1 block w-full px-4 py-2 transition-colors focus:outline-none',
            'text-body1 text-primary-13 caret-primary-13 placeholder:text-primary-10',
            'aria-[invalid=true]:border-error-8'
        ],
        textSlot: 'ml-4 flex justify-between text-body3',
        helperTextSlot: 'text-primary-13',
        errorTextSlot: 'col-start-1 row-start-1 self-center justify-self-end text-error-8',
        successTextSlot: 'col-start-1 row-start-1 self-center justify-self-end',
        characterCount: 'text-right text-neutral-10',
        iconLeft: 'pointer-events-none col-start-1 row-start-1 self-center pl-4 text-primary-8',
        iconRight: 'pointer-events-none col-start-1 row-start-1 self-center justify-self-end pr-4 text-primary-6',
        rightIconButton: [
            'mr-2 cursor-pointer'
        ]
    },
    variants: {
        isInset: {
            true: {
                labelSlot: [
                    'pointer-events-none absolute right-[17px] left-[17px] mb-0 truncate text-primary-10',
                    'group-data-focused:text-body3 group-data-focused:text-primary-13',
                    'group-data-filled:text-body3 group-data-filled:text-primary-13'
                ],
                input: 'translate-y-2 group-data-filled:text-primary-13 group-data-filled:placeholder:invisible'
            }
        },
        size: {
            sm: {
                inputWrapper: 'h-[32px] rounded-xl',
                labelSlot: 'group-data-filled:text-body3',
                input: 'py-1 group-data-filled:text-body1'
            },
            md: {
                inputWrapper: 'h-[48px]',
                labelSlot: 'group-data-filled:text-body3',
                input: 'group-data-filled:text-body1'
            },
            lg: {
                inputWrapper: 'h-[56px]',
                labelSlot: 'group-data-filled:text-body3',
                input: 'group-data-filled:text-body1'
            }
        },
        emphasis: {
            regular: {},
            medium: {},
            bold: {
                inputWrapper: [
                    'bg-primary-1/20',
                    'border-primary-1/40 focus-within:border-primary-4'
                ],
                labelSlot: [
                    'text-primary-1 group-data-filled:text-primary-1 group-data-focused:text-primary-1',
                    'not-group-data-filled:text-primary-1'
                ],
                input: 'text-primary-1 caret-primary-1 group-data-filled:text-primary-1 placeholder:text-primary-6',
                iconLeft: 'text-primary-1',
                iconRight: 'text-primary-1',
                helperTextSlot: 'text-primary-1'
            }
        },
        hasIconLeft: {
            true: {
                input: 'pl-13'
            }
        },
        hasIconRight: {
            true: {
                input: 'pr-13'
            }
        },
        hasPlaceholder: {
            true: {}
        },
        rightIconButton: {
            true: {
                inputWrapper: 'grid-cols-[1fr_auto] gap-x-0'
            }
        },
        isDisabled: {
            true: {
                inputWrapper: 'cursor-not-allowed bg-neutral-2 disabled:text-primary-6',
                labelSlot: 'cursor-not-allowed opacity-50 disabled:text-primary-6',
                input: 'cursor-not-allowed rounded-xl bg-neutral-2 caret-primary-6 opacity-50 disabled:text-primary-6'
            },
            false: {
                inputWrapper: [
                    'transition hover:border-primary-10 group-data-focused:hover:border-primary-8',
                    'transition hover:shadow-input-hover'
                ]
            }
        },
        error: {
            true: {
                input: 'pr-8',
                inputWrapper: [
                    'border-error-7',
                    'transition group-data-focused:shadow-input-error hover:shadow-input-error'
                ]
            },
            false: {
                inputWrapper: [
                    'focus-within:ring-2 focus-within:ring-primary-8/20'
                ]
            }
        },
        success: {
            true: {
                input: 'pr-8'
            },
            false: {}
        }
    },
    compoundVariants: [
        {
            isInset: true,
            size: 'sm',
            class: {
                labelSlot: [
                    'not-group-data-filled:translate-y-1 not-group-data-filled:text-body1',
                    'group-data-filled:translate-y-0',
                    'group-data-focused:translate-y-0'
                ],
                input: 'translate-y-1.5'
            }
        },
        {
            isInset: true,
            hasPlaceholder: true,
            size: 'sm',
            class: {
                labelSlot: 'not-group-data-filled:translate-y-0'
            }
        },
        {
            isInset: true,
            size: 'md',
            class: {
                labelSlot: [
                    'not-group-data-filled:translate-y-3 not-group-data-filled:text-body1',
                    'group-data-filled:translate-y-1.5',
                    'group-data-focused:translate-y-1.5'
                ]
            }
        },
        {
            isInset: true,
            hasPlaceholder: true,
            size: 'md',
            class: {
                labelSlot: 'not-group-data-filled:translate-y-1.5'
            }
        },
        {
            isInset: true,
            size: 'lg',
            class: {
                labelSlot: [
                    'not-group-data-filled:translate-y-4 not-group-data-filled:text-body1',
                    'group-data-filled:translate-y-2',
                    'group-data-focused:translate-y-2'
                ]
            }
        },
        {
            isInset: true,
            hasPlaceholder: true,
            size: 'lg',
            class: {
                labelSlot: 'not-group-data-filled:translate-y-2'
            }
        },
        {
            hasPlaceholder: true,
            isInset: true,
            class: {
                labelSlot: 'text-body3 text-primary-13 not-group-data-filled:text-body3',
                input: 'no-group-data-focused:placeholder:visible group-data-filled:placeholder:visible'
            }
        },
        {
            hasIconLeft: true,
            class: {
                input: 'ml-0'
            }
        },
        {
            hasIconLeft: true,
            isInset: true,
            class: {
                labelSlot: 'left-13 ml-[1px]'
            }
        },
        {
            isDisabled: true,
            emphasis: 'regular',
            class: {
                inputWrapper: 'cursor-not-allowed border-primary-5 bg-neutral-2 disabled:text-primary-6',
                labelSlot: 'cursor-not-allowed opacity-50 disabled:text-primary-6',
                input: 'cursor-not-allowed bg-neutral-2 opacity-75 disabled:text-primary-6',
                iconLeft: 'text-primary-6',
                iconRight: 'text-primary-6'
            }
        },
        {
            isDisabled: true,
            emphasis: 'medium',
            class: {
                inputWrapper: 'cursor-not-allowed border-primary-5 bg-neutral-2 disabled:text-primary-8',
                labelSlot: 'cursor-not-allowed opacity-50 disabled:text-primary-6',
                input: 'cursor-not-allowed bg-neutral-2 opacity-75 disabled:text-primary-6',
                iconLeft: 'text-primary-6',
                iconRight: 'text-primary-6'
            }
        },
        {
            isDisabled: true,
            emphasis: 'bold',
            class: {
                inputWrapper: [
                    'bg-primary-1/30',
                    'border-primary-1/10'
                ],
                labelSlot: [
                    'text-primary-6 opacity-50 disabled:text-primary-6'
                ],
                input: [
                    'opacity-30 disabled:text-primary-6',
                    'bg-transparent'
                ],
                iconLeft: 'text-primary-6/40',
                iconRight: 'text-primary-6/40'
            }
        },
        {
            error: true,
            emphasis: 'bold',
            class: {
                inputWrapper: [
                    'bg-primary-1/20',
                    'transition hover:border-error-4',
                    'focus-within:border-error-4',
                    'transition group-data-focused:hover:border-error-7'
                ],
                labelSlot: [
                    'text-primary-1 group-data-filled:text-primary-1 group-data-focused:text-primary-1',
                    'not-group-data-filled:text-primary-1'
                ],
                input: [
                    'text-primary-1 group-data-filled:text-primary-1 placeholder:text-primary-6',
                    'bg-transparent'
                ],
                errorTextSlot: 'text-error-7'
            }
        },
        {
            error: true,
            emphasis: 'medium',
            class: {
                inputWrapper: [
                    'transition hover:border-error-8',
                    'focus-within:border-error-8',
                    'transition group-data-focused:hover:border-error-8',
                    'transition group-data-focused:shadow-input-error hover:shadow-input-error'
                ]
            }
        },
        {
            error: true,
            emphasis: 'regular',
            class: {
                inputWrapper: [
                    'bg-primary-1/20',
                    'transition hover:border-error-8',
                    'focus-within:border-error-8',
                    'transition group-data-focused:hover:border-error-8',
                    'transition group-data-focused:shadow-input-error hover:shadow-input-error'
                ]
            }
        },
        {
            size: 'sm',
            class: {
                errorTextSlot: 'pr-1',
                successTextSlot: 'pr-1'
            }
        },
        {
            size: 'md',
            class: {
                errorTextSlot: 'pr-2',
                successTextSlot: 'pr-2'
            }
        },
        {
            size: 'lg',
            class: {
                errorTextSlot: 'pr-2',
                successTextSlot: 'pr-2'
            }
        }
    ],
    defaultVariants: {
        emphasis: 'regular',
        isInset: false,
        size: 'md'
    }
});
const inputTextSizes = Object.keys(inputTextVariants.variants.size);
const inputTextEmphases = Object.keys(inputTextVariants.variants.emphasis);
export { inputTextEmphases, inputTextSizes, inputTextVariants };
